import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Search, Edit3, Trash2, Copy, Eye, EyeOff, X, Save, ChevronDown, GripVertical, Upload, Link2, ExternalLink, Star, Calendar, FileText } from 'lucide-react';
import { API } from '../../contexts/AuthContext';
import { FileUpload } from '../../components/admin/FileUpload';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const Quill = ReactQuill.Quill;
const Font = Quill.import('formats/font');
Font.whitelist = ['inter', 'poppins', 'roboto', 'playfair-display', 'lora', 'nunito', 'open-sans', 'serif', 'monospace'];
Quill.register(Font, true);

const CATEGORIES = [
  { value: 'company', label: 'Company', color: '#0c3c60' },
  { value: 'product', label: 'Product', color: '#059669' },
  { value: 'innovation', label: 'Innovation', color: '#2563eb' },
  { value: 'event', label: 'Event', color: '#7c3aed' },
  { value: 'achievement', label: 'Achievement', color: '#d97706' },
  { value: 'publication', label: 'Publication', color: '#0891b2' },
  { value: 'partnership', label: 'Partnership', color: '#0d9488' },
  { value: 'announcement', label: 'Announcement', color: '#dc2626' },
  { value: 'other', label: 'Other', color: '#6b7280' },
];

const ICONS = [
  { value: 'building', label: 'Building' },
  { value: 'medicine', label: 'Medicine' },
  { value: 'flask', label: 'Flask / Innovation' },
  { value: 'calendar', label: 'Calendar' },
  { value: 'trophy', label: 'Trophy' },
  { value: 'document', label: 'Document' },
  { value: 'handshake', label: 'Handshake' },
  { value: 'megaphone', label: 'Megaphone' },
  { value: 'star', label: 'Star' },
];

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const STATUSES = [{ v: 'draft', l: 'Draft', c: '#6b7280' }, { v: 'published', l: 'Published', c: '#059669' }, { v: 'scheduled', l: 'Scheduled', c: '#d97706' }, { v: 'archived', l: 'Archived', c: '#9ca3af' }];

const catColor = (cat) => CATEGORIES.find(c => c.value === cat)?.color || '#6b7280';

const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, 4, false] }],
    [{ 'font': Font.whitelist }],
    [{ 'size': ['small', false, 'large', 'huge'] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    [{ 'align': [] }],
    ['blockquote', 'code-block'],
    ['link', 'image', 'video'],
    [{ 'indent': '-1' }, { 'indent': '+1' }],
    ['clean']
  ]
};

const emptyForm = () => ({
  year: new Date().getFullYear(), month: new Date().getMonth() + 1, date: new Date().getDate(),
  category: 'company', icon: 'building', heading: '', short_description: '', content: '',
  featured_image: '', gallery: [], video_url: '', attachments: [], buttons: [],
  status: 'draft', featured: false, show_on_homepage: false,
  seo_title: '', meta_description: '', seo_keywords: '', slug: '', social_share_image: '',
  publish_date: '', expiry_date: '', order: 0,
});

export default function ArchiveAdmin() {
  const [archives, setArchives] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [filters, setFilters] = useState({ year: 'all', category: 'all', status: 'all', search: '' });
  const [toast, setToast] = useState('');
  const [saving, setSaving] = useState(false);
  const [seoOpen, setSeoOpen] = useState(false);

  const token = localStorage.getItem('idsea_token');
  const headers = { Authorization: `Bearer ${token}` };
  const showToast = (m) => { setToast(m); setTimeout(() => setToast(''), 3000); };

  const load = () => {
    const p = {};
    if (filters.year !== 'all') p.year = filters.year;
    if (filters.category !== 'all') p.category = filters.category;
    if (filters.status !== 'all') p.status = filters.status;
    if (filters.search) p.search = filters.search;
    axios.get(`${API}/admin/archives`, { params: p, headers }).then(r => setArchives(r.data || [])).catch(() => {});
  };

  useEffect(() => { load(); }, [filters]);

  const u = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const openNew = () => { setEditId(null); setForm(emptyForm()); setShowEditor(true); };
  const openEdit = (a) => { setEditId(a.id); setForm({ ...emptyForm(), ...a }); setShowEditor(true); };

  const handleSave = async () => {
    if (!form.heading) { showToast('Heading is required'); return; }
    setSaving(true);
    try {
      if (editId) {
        await axios.put(`${API}/admin/archives/${editId}`, form, { headers });
        showToast('Archive updated');
      } else {
        await axios.post(`${API}/admin/archives`, form, { headers });
        showToast('Archive created');
      }
      setShowEditor(false); load();
    } catch (e) { showToast(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const handleDelete = async (id) => { if (!window.confirm('Delete this archive entry?')) return; await axios.delete(`${API}/admin/archives/${id}`, { headers }); showToast('Deleted'); load(); };
  const handleDuplicate = async (id) => { await axios.post(`${API}/admin/archives/${id}/duplicate`, {}, { headers }); showToast('Duplicated'); load(); };
  const toggleStatus = async (a) => {
    const next = a.status === 'published' ? 'draft' : 'published';
    await axios.put(`${API}/admin/archives/${a.id}`, { status: next }, { headers }); showToast(`Set to ${next}`); load();
  };

  // Button helpers
  const addButton = () => u('buttons', [...form.buttons, { name: '', url: '', style: 'primary', new_tab: false, order: form.buttons.length }]);
  const updateButton = (i, k, v) => { const b = [...form.buttons]; b[i] = { ...b[i], [k]: v }; u('buttons', b); };
  const removeButton = (i) => u('buttons', form.buttons.filter((_, idx) => idx !== i));

  // Attachment helpers
  const addAttachment = (url, fname) => u('attachments', [...form.attachments, { file_url: url, file_name: fname, display_name: fname, file_type: fname.split('.').pop()?.toUpperCase() || '', order: form.attachments.length }]);
  const updateAttachment = (i, k, v) => { const a = [...form.attachments]; a[i] = { ...a[i], [k]: v }; u('attachments', a); };
  const removeAttachment = (i) => u('attachments', form.attachments.filter((_, idx) => idx !== i));

  // Gallery helpers
  const addGalleryImage = (url) => u('gallery', [...form.gallery, url]);
  const removeGalleryImage = (i) => u('gallery', form.gallery.filter((_, idx) => idx !== i));

  const years = [...new Set(archives.map(a => a.year))].sort((a, b) => b - a);

  if (showEditor) return (
    <div>
      {toast && <div className="toast-success" data-testid="toast">{toast}</div>}
      <div className="page-header">
        <h1 className="page-title">{editId ? 'Edit Archive' : 'New Archive Entry'}</h1>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => setShowEditor(false)} className="btn-secondary">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="save-archive-btn"><Save size={14} /> {saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: '20px' }}>
        {/* Main Content */}
        <div>
          <div className="admin-card" style={{ marginBottom: '16px' }}>
            <h3 style={{ fontFamily: 'Poppins', fontSize: '14px', fontWeight: 700, color: '#0c3c60', marginBottom: '14px' }}>Basic Information</h3>
            <div className="form-group"><label className="form-label">Heading / Title *</label><input value={form.heading} onChange={e => u('heading', e.target.value)} className="form-input" placeholder="Archive entry title" data-testid="archive-heading" /></div>
            <div className="form-group"><label className="form-label">Short Description</label><textarea value={form.short_description} onChange={e => u('short_description', e.target.value)} className="form-textarea" rows={2} placeholder="Brief excerpt shown on timeline..." /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
              <div className="form-group"><label className="form-label">Year *</label><input type="number" value={form.year} onChange={e => u('year', parseInt(e.target.value))} className="form-input" data-testid="archive-year" /></div>
              <div className="form-group"><label className="form-label">Month *</label>
                <select value={form.month} onChange={e => u('month', parseInt(e.target.value))} className="form-select">
                  {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Date *</label><input type="number" min={1} max={31} value={form.date} onChange={e => u('date', parseInt(e.target.value))} className="form-input" /></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div className="form-group"><label className="form-label">Category *</label>
                <select value={form.category} onChange={e => u('category', e.target.value)} className="form-select" data-testid="archive-category">
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Icon</label>
                <select value={form.icon} onChange={e => u('icon', e.target.value)} className="form-select">
                  {ICONS.map(ic => <option key={ic.value} value={ic.value}>{ic.label}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div className="admin-card" style={{ marginBottom: '16px' }}>
            <h3 style={{ fontFamily: 'Poppins', fontSize: '14px', fontWeight: 700, color: '#0c3c60', marginBottom: '14px' }}>Rich Text Content</h3>
            <ReactQuill value={form.content || ''} onChange={val => u('content', val)} theme="snow" modules={quillModules} style={{ background: 'white', borderRadius: '8px', minHeight: '250px' }} />
          </div>

          {/* Action Buttons */}
          <div className="admin-card" style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <h3 style={{ fontFamily: 'Poppins', fontSize: '14px', fontWeight: 700, color: '#0c3c60', margin: 0 }}>Action Buttons</h3>
              <button onClick={addButton} className="btn-secondary" style={{ fontSize: '12px', padding: '4px 12px' }}><Plus size={13} /> Add Button</button>
            </div>
            {form.buttons.map((btn, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto auto auto', gap: '8px', alignItems: 'end', marginBottom: '8px', padding: '10px', background: '#f8fafc', borderRadius: '8px' }}>
                <div className="form-group" style={{ margin: 0 }}><label className="form-label" style={{ fontSize: '11px' }}>Button Name</label><input value={btn.name} onChange={e => updateButton(i, 'name', e.target.value)} className="form-input" placeholder="Read More" /></div>
                <div className="form-group" style={{ margin: 0 }}><label className="form-label" style={{ fontSize: '11px' }}>URL</label><input value={btn.url} onChange={e => updateButton(i, 'url', e.target.value)} className="form-input" placeholder="https://..." /></div>
                <select value={btn.style || 'primary'} onChange={e => updateButton(i, 'style', e.target.value)} className="form-select" style={{ width: '100px', fontSize: '11px' }}>
                  <option value="primary">Primary</option><option value="secondary">Secondary</option><option value="outline">Outline</option>
                </select>
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  <input type="checkbox" checked={btn.new_tab || false} onChange={e => updateButton(i, 'new_tab', e.target.checked)} /> New Tab
                </label>
                <button onClick={() => removeButton(i)} style={{ background: '#fee2e2', color: '#dc2626', border: 'none', padding: '6px', borderRadius: '6px', cursor: 'pointer' }}><Trash2 size={13} /></button>
              </div>
            ))}
            {form.buttons.length === 0 && <p style={{ color: '#9ca3af', fontSize: '12px' }}>No buttons added yet.</p>}
          </div>

          {/* Attachments */}
          <div className="admin-card" style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <h3 style={{ fontFamily: 'Poppins', fontSize: '14px', fontWeight: 700, color: '#0c3c60', margin: 0 }}>Attachments</h3>
              <FileUpload accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.png,.webp,.mp4" label="+ Add File" onUpload={(url) => addAttachment(url, url.split('/').pop())} />
            </div>
            {form.attachments.map((att, i) => (
              <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px', padding: '8px', background: '#f8fafc', borderRadius: '8px' }}>
                <FileText size={14} style={{ color: '#2563eb', flexShrink: 0 }} />
                <input value={att.display_name || ''} onChange={e => updateAttachment(i, 'display_name', e.target.value)} className="form-input" style={{ flex: 1, fontSize: '12px', padding: '4px 8px' }} placeholder="Display name" />
                <span style={{ fontSize: '10px', color: '#6b7280', fontWeight: 600, whiteSpace: 'nowrap' }}>{att.file_type}</span>
                <button onClick={() => removeAttachment(i)} style={{ background: '#fee2e2', color: '#dc2626', border: 'none', padding: '4px', borderRadius: '4px', cursor: 'pointer' }}><X size={12} /></button>
              </div>
            ))}
            {form.attachments.length === 0 && <p style={{ color: '#9ca3af', fontSize: '12px' }}>No attachments. Upload files above.</p>}
          </div>

          {/* SEO */}
          <div className="admin-card">
            <button onClick={() => setSeoOpen(!seoOpen)} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'Poppins', fontSize: '14px', fontWeight: 700, color: '#0c3c60', width: '100%', justifyContent: 'space-between' }}>
              SEO Settings <ChevronDown size={16} style={{ transform: seoOpen ? 'rotate(180deg)' : '', transition: '0.2s' }} />
            </button>
            {seoOpen && (
              <div style={{ marginTop: '14px' }}>
                <div className="form-group"><label className="form-label">SEO Title</label><input value={form.seo_title} onChange={e => u('seo_title', e.target.value)} className="form-input" /></div>
                <div className="form-group"><label className="form-label">Meta Description</label><textarea value={form.meta_description} onChange={e => u('meta_description', e.target.value)} className="form-textarea" rows={2} /></div>
                <div className="form-group"><label className="form-label">Keywords</label><input value={form.seo_keywords} onChange={e => u('seo_keywords', e.target.value)} className="form-input" placeholder="comma, separated, keywords" /></div>
                <div className="form-group"><label className="form-label">URL Slug</label><input value={form.slug} onChange={e => u('slug', e.target.value)} className="form-input" placeholder="auto-generated-from-title" /></div>
                <div className="form-group"><label className="form-label">Social Share Image</label><FileUpload accept="image/*" label="Upload" onUpload={url => u('social_share_image', url)} /></div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div>
          <div className="admin-card" style={{ marginBottom: '16px' }}>
            <h3 style={{ fontFamily: 'Poppins', fontSize: '14px', fontWeight: 700, color: '#0c3c60', marginBottom: '14px' }}>Publishing</h3>
            <div className="form-group"><label className="form-label">Status</label>
              <select value={form.status} onChange={e => u('status', e.target.value)} className="form-select" data-testid="archive-status">
                {STATUSES.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
              </select>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer', marginBottom: '10px' }}>
              <input type="checkbox" checked={form.featured} onChange={e => u('featured', e.target.checked)} /> <Star size={14} style={{ color: '#d97706' }} /> Featured
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer', marginBottom: '10px' }}>
              <input type="checkbox" checked={form.show_on_homepage} onChange={e => u('show_on_homepage', e.target.checked)} /> Show on Homepage
            </label>
            <div className="form-group"><label className="form-label">Publish Date</label><input type="date" value={form.publish_date} onChange={e => u('publish_date', e.target.value)} className="form-input" /></div>
            <div className="form-group"><label className="form-label">Expiry Date</label><input type="date" value={form.expiry_date} onChange={e => u('expiry_date', e.target.value)} className="form-input" /></div>
          </div>

          <div className="admin-card" style={{ marginBottom: '16px' }}>
            <h3 style={{ fontFamily: 'Poppins', fontSize: '14px', fontWeight: 700, color: '#0c3c60', marginBottom: '14px' }}>Featured Image</h3>
            <FileUpload accept="image/*" label="Upload Image" onUpload={url => u('featured_image', url)} />
            {form.featured_image && (
              <div style={{ marginTop: '10px', position: 'relative' }}>
                <img src={form.featured_image.startsWith('/api') ? `${API.replace('/api', '')}${form.featured_image}` : form.featured_image} alt="" style={{ width: '100%', borderRadius: '10px', objectFit: 'cover', maxHeight: '180px' }} />
                <button onClick={() => u('featured_image', '')} style={{ position: 'absolute', top: '6px', right: '6px', width: '22px', height: '22px', borderRadius: '50%', background: '#ef4444', color: 'white', border: 'none', cursor: 'pointer', fontSize: '12px' }}>&times;</button>
              </div>
            )}
          </div>

          <div className="admin-card" style={{ marginBottom: '16px' }}>
            <h3 style={{ fontFamily: 'Poppins', fontSize: '14px', fontWeight: 700, color: '#0c3c60', marginBottom: '14px' }}>Gallery</h3>
            <FileUpload accept="image/*" label="+ Add Image" onUpload={addGalleryImage} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginTop: '10px' }}>
              {form.gallery.map((img, i) => (
                <div key={i} style={{ position: 'relative' }}>
                  <img src={img.startsWith('/api') ? `${API.replace('/api', '')}${img}` : img} alt="" style={{ width: '100%', height: '70px', objectFit: 'cover', borderRadius: '6px' }} />
                  <button onClick={() => removeGalleryImage(i)} style={{ position: 'absolute', top: '2px', right: '2px', width: '18px', height: '18px', borderRadius: '50%', background: '#ef4444', color: 'white', border: 'none', cursor: 'pointer', fontSize: '10px' }}>&times;</button>
                </div>
              ))}
            </div>
          </div>

          <div className="admin-card">
            <h3 style={{ fontFamily: 'Poppins', fontSize: '14px', fontWeight: 700, color: '#0c3c60', marginBottom: '14px' }}>Video</h3>
            <input value={form.video_url} onChange={e => u('video_url', e.target.value)} className="form-input" placeholder="YouTube/Vimeo embed URL" />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      {toast && <div className="toast-success" data-testid="toast">{toast}</div>}
      <div className="page-header">
        <h1 className="page-title" data-testid="archive-admin-title">Archive & Timeline</h1>
        <button onClick={openNew} className="btn-primary" data-testid="add-archive-btn"><Plus size={16} /> Add Archive</button>
      </div>

      {/* Filters */}
      <div className="admin-card" style={{ marginBottom: '16px', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
          <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} className="form-input" style={{ paddingLeft: '32px' }} placeholder="Search archives..." data-testid="archive-search" />
        </div>
        <select value={filters.year} onChange={e => setFilters(f => ({ ...f, year: e.target.value }))} className="form-select" style={{ width: '120px' }}>
          <option value="all">All Years</option>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <select value={filters.category} onChange={e => setFilters(f => ({ ...f, category: e.target.value }))} className="form-select" style={{ width: '140px' }}>
          <option value="all">All Categories</option>
          {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))} className="form-select" style={{ width: '130px' }}>
          <option value="all">All Status</option>
          {STATUSES.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="admin-card">
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Inter, sans-serif' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', fontFamily: 'Poppins' }}>Date</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', fontFamily: 'Poppins' }}>Heading</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', fontFamily: 'Poppins' }}>Category</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', fontFamily: 'Poppins' }}>Status</th>
                <th style={{ padding: '12px', textAlign: 'center', fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', fontFamily: 'Poppins' }}>Featured</th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', fontFamily: 'Poppins' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {archives.length === 0 && <tr><td colSpan={6} style={{ padding: '48px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>No archive entries yet. Click "Add Archive" to create your first entry.</td></tr>}
              {archives.map(a => (
                <tr key={a.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '12px', fontSize: '13px', color: '#374151', fontWeight: 600, whiteSpace: 'nowrap' }}>{a.date} {MONTHS[a.month - 1]?.slice(0, 3)} {a.year}</td>
                  <td style={{ padding: '12px', fontSize: '13px', fontWeight: 600, color: '#111827', maxWidth: '300px' }}>{a.heading}</td>
                  <td style={{ padding: '12px' }}><span style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '12px', background: `${catColor(a.category)}15`, color: catColor(a.category), fontWeight: 700, textTransform: 'capitalize' }}>{a.category}</span></td>
                  <td style={{ padding: '12px' }}><span style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '12px', background: STATUSES.find(s => s.v === a.status)?.c + '20' || '#f3f4f6', color: STATUSES.find(s => s.v === a.status)?.c || '#6b7280', fontWeight: 700 }}>{a.status}</span></td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>{a.featured && <Star size={14} style={{ color: '#d97706' }} fill="#d97706" />}</td>
                  <td style={{ padding: '12px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                      <button onClick={() => openEdit(a)} title="Edit" style={{ padding: '5px 7px', background: '#eff6ff', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#2563eb' }}><Edit3 size={13} /></button>
                      <button onClick={() => handleDuplicate(a.id)} title="Duplicate" style={{ padding: '5px 7px', background: '#f0fdf4', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#059669' }}><Copy size={13} /></button>
                      <button onClick={() => toggleStatus(a)} title={a.status === 'published' ? 'Unpublish' : 'Publish'} style={{ padding: '5px 7px', background: a.status === 'published' ? '#fef3c7' : '#d1fae5', border: 'none', borderRadius: '6px', cursor: 'pointer', color: a.status === 'published' ? '#92400e' : '#065f46' }}>
                        {a.status === 'published' ? <EyeOff size={13} /> : <Eye size={13} />}
                      </button>
                      <button onClick={() => handleDelete(a.id)} title="Delete" style={{ padding: '5px 7px', background: '#fee2e2', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#dc2626' }}><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
